import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Checkbox, FormControlLabel, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import LoadingIndicator from '../components/LoadingIndicator';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import { LOGIN_PREFERENCES, useAuth } from '../AuthContext';
import Turnstile from '../components/Turnstile';
import { getLoginConfig } from '../api/axios';

export default function TenantLogin() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const destination = typeof location.state?.from === 'string' && location.state.from.startsWith('/sysadmin') ? location.state.from : '/dashboard';
  const [prefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LOGIN_PREFERENCES)) || {}; } catch { return {}; }
  });
  const [tenant, setTenant] = useState(typeof prefs.tenant === 'string' ? prefs.tenant : '');
  const [username, setUsername] = useState(typeof prefs.username === 'string' ? prefs.username : '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(false);
  const [configAttempt, setConfigAttempt] = useState(0);
  useEffect(() => {
    let mounted = true;
    setConfig(null); setConfigError(false);
    getLoginConfig().then(value => { if (mounted) setConfig(value); })
      .catch(() => { if (mounted) setConfigError(true); });
    return () => { mounted = false; };
  }, [configAttempt]);
  if (auth.loading) return <LoadingIndicator label="Sitzung wird geprüft" sx={{ p: 4 }} />;
  if (auth.user) return <Navigate to={destination} replace />;
  const submit = async event => {
    event.preventDefault();
    if (!config || (config.turnstile_enabled && !captcha) || busy) return;
    // Autofill can update native inputs without a React change event. Submit
    // the values the browser actually displays, then synchronize local state.
    const fields = new FormData(event.currentTarget);
    const submittedTenant = fields.get('tenant');
    const submittedUsername = fields.get('username');
    const submittedPassword = fields.get('password');
    setTenant(submittedTenant); setUsername(submittedUsername); setPassword(submittedPassword);
    setBusy(true); setError('');
    try {
      await auth.login({ tenant: submittedTenant, username: submittedUsername, password: submittedPassword,
        remember, ...(config.turnstile_enabled ? { captcha } : {}) });
      setPassword('');
      navigate(destination, { replace: true });
    } catch (failure) {
      const status = failure.response?.status;
      setError(!failure.response ? 'Anmeldedienst nicht erreichbar. Bitte Verbindung prüfen und erneut versuchen.'
        : status === 403 ? 'Anmeldung von dieser Adresse wurde abgewiesen. Bitte die reguläre Anmeldeseite verwenden.'
        : status === 503 ? 'Anmeldung derzeit nicht verfügbar. Bitte erneut versuchen.'
        : status === 401 ? 'Anmeldung fehlgeschlagen. Bitte überprüfe deine Zugangsdaten.'
        : 'Anmeldung konnte nicht abgeschlossen werden. Bitte erneut versuchen.');
      setPassword(''); setCaptcha(''); setAttempt(value => value + 1);
    } finally { setBusy(false); }
  };
  return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
    <Paper elevation={3} sx={{ width: '100%', maxWidth: 420, boxSizing: 'border-box', p: { xs: 2.5, sm: 4 }, borderRadius: 2 }}>
      <Typography variant="h5" component="h1" color="primary" sx={{ mb: 1 }}>Jagdverwaltung</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Bei deinem Mandanten anmelden</Typography>
      <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField name="tenant" label="Mandant" required size="small" autoComplete="organization" value={tenant} onChange={event => setTenant(event.target.value)} inputProps={{ maxLength: 255 }} />
        <TextField name="username" label="Benutzername oder E-Mail" required size="small" autoComplete="username" value={username} onChange={event => setUsername(event.target.value)} inputProps={{ maxLength: 254 }} />
        <TextField name="password" label="Passwort" required size="small" type={showPassword ? 'text' : 'password'} autoComplete="current-password"
          value={password} onChange={event => setPassword(event.target.value)} inputProps={{ maxLength: 1024 }}
          InputProps={{ endAdornment: <InputAdornment position="end"><IconButton edge="end" aria-label={showPassword ? 'Passwort ausblenden' : 'Passwort anzeigen'}
            onClick={() => setShowPassword(value => !value)} onMouseDown={event => event.preventDefault()}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
        <FormControlLabel control={<Checkbox checked={remember} onChange={event => setRemember(event.target.checked)} />} label="Angemeldet bleiben" />
        {!config && !configError && <LoadingIndicator label="Anmeldekonfiguration wird geladen …" />}
        {busy && <LoadingIndicator label="Anmeldung läuft …" compact />}
        {configError && <Alert severity="error" action={<Button onClick={() => setConfigAttempt(value => value + 1)}>Erneut laden</Button>}>Anmeldekonfiguration konnte nicht geladen werden.</Alert>}
        {config?.turnstile_enabled && <Turnstile onToken={setCaptcha} attempt={attempt} />}
        {error && <Alert severity="error">{error}</Alert>}
        <Button type="submit" variant="contained" startIcon={<LoginIcon />} disabled={!config || (config.turnstile_enabled && !captcha) || busy}>{busy ? 'Anmeldung läuft …' : 'Anmelden'}</Button>
      </Box>
    </Paper>
  </Box>;
}
